import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import BackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/EditOutlined";
import StarIcon from "@mui/icons-material/Star";
import LayersIcon from "@mui/icons-material/Layers";
import UploadIcon from "@mui/icons-material/CloudUpload";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import PdfIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import type { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { TextField } from "../../../components/ui";
import {
  showErrorToast,
  showInfoToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import apiService from "../../../service/Api";

const PAGE_COUNTS = [4, 8, 12, 16];

// --- API shapes (backend/configuration/serializers.py) -------------------

type ApiProgramTemplate = {
  id: number;
  name: string;
  description: string;
  category: "classic" | "botanical" | "faith" | "traditional" | "celebration_of_life" | "other";
  theme_color: string;
  cover_image: string | null;
  background_image: string | null;
  sample_pdf: string | null;
  is_active: boolean;
  is_featured: boolean;
  sort_order: number;
  company: number;
  created_at: string;
  updated_at: string;
};

type ApiProgramPaperOption = {
  id: number;
  name: string;
  multiplier: string;
  weight_label: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  company: number;
};

type ApiProgramAddOn = {
  id: number;
  key: string;
  label: string;
  description: string;
  price: string;
  is_active: boolean;
  company: number;
};

type ApiProgramPricingConfig = {
  id: number;
  company: number;
  base_price: string;
  baseline_pages: number;
  baseline_copies: number;
  updated_at: string;
};

type PaginatedResponse<T> = { count: number; next: string | null; previous: string | null; results: T[] };

const CATEGORY_OPTIONS: { value: ApiProgramTemplate["category"]; label: string }[] = [
  { value: "classic", label: "Classic" },
  { value: "botanical", label: "Botanical" },
  { value: "faith", label: "Faith" },
  { value: "traditional", label: "Traditional" },
  { value: "celebration_of_life", label: "Celebration of Life" },
  { value: "other", label: "Other" },
];

function categoryLabel(category: ApiProgramTemplate["category"]) {
  return CATEGORY_OPTIONS.find((option) => option.value === category)?.label ?? category;
}

function fileNameFromUrl(url: string | null): string {
  if (!url) return "";
  try {
    return decodeURIComponent(url.split("/").pop() ?? "");
  } catch {
    return url;
  }
}

// --- Pricing formula -------------------------------------------------------
// base price x (pages / baseline pages) x (copies / baseline copies) x paper
// multiplier, instead of a hand-filled pages x copies x paper price matrix --
// the old matrix's numbers were themselves generated by this exact formula,
// so editing cells by hand was never independent data.
function computePrice(basePrice: number, baselinePages: number, baselineCopies: number, copies: number, pages: number, paperMultiplier: number) {
  return basePrice * (pages / baselinePages) * (copies / baselineCopies) * paperMultiplier;
}

type EditorValue = {
  name: string;
  description: string;
  category: ApiProgramTemplate["category"];
  isActive: boolean;
  themeColor: string;
  isFeatured: boolean;
  sortOrder: number;
  coverImagePreview: string | null;
  coverImageFile: File | null;
  backgroundImagePreview: string | null;
  backgroundImageFile: File | null;
  samplePdfName: string | null;
  samplePdfFile: File | null;
};

const EMPTY_EDITOR: EditorValue = {
  name: "",
  description: "",
  category: "classic",
  isActive: true,
  themeColor: "",
  isFeatured: false,
  sortOrder: 1,
  coverImagePreview: null,
  coverImageFile: null,
  backgroundImagePreview: null,
  backgroundImageFile: null,
  samplePdfName: null,
  samplePdfFile: null,
};

const ProgramBuilderTemplates: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const brandColor = colors.primary[400];
  const brandTextColor = theme.palette.getContrastText(brandColor);
  const isDark = theme.palette.mode === "dark";
  const { hasPermission } = usePermissions();

  const [templates, setTemplates] = useState<ApiProgramTemplate[]>([]);
  const [paperOptions, setPaperOptions] = useState<ApiProgramPaperOption[]>([]);
  const [addOns, setAddOns] = useState<ApiProgramAddOn[]>([]);
  const [pricing, setPricing] = useState<ApiProgramPricingConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [activeTab, setActiveTab] = useState<"templates" | "pricing" | "addons" | "paper">("templates");
  const [copyQuantities, setCopyQuantities] = useState([50, 100, 150, 200]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ApiProgramTemplate | null>(null);
  const [editor, setEditor] = useState<EditorValue>(EMPTY_EDITOR);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [templatesRes, papersRes, addOnsRes, pricingRes] = await Promise.all([
        apiService.get<PaginatedResponse<ApiProgramTemplate>>("/api/configurations/program-templates/", { page_size: 200 }),
        apiService.get<PaginatedResponse<ApiProgramPaperOption>>("/api/configurations/program-paper-options/", { page_size: 200 }),
        apiService.get<PaginatedResponse<ApiProgramAddOn>>("/api/configurations/program-add-ons/", { page_size: 200 }),
        apiService.get<ApiProgramPricingConfig>("/api/configurations/program-pricing-config/mine/"),
      ]);
      setTemplates(templatesRes.data.results ?? []);
      setPaperOptions(papersRes.data.results ?? []);
      setAddOns(addOnsRes.data.results ?? []);
      setPricing(pricingRes.data);
    } catch (error) {
      console.error("Failed to load program builder configuration", error);
      setLoadError("Program builder configuration could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const openCreate = () => {
    setEditing(null);
    setEditor({ ...EMPTY_EDITOR, themeColor: brandColor, sortOrder: templates.length + 1 });
    setDialogOpen(true);
  };

  const openEdit = (template: ApiProgramTemplate) => {
    setEditing(template);
    setEditor({
      name: template.name,
      description: template.description || "",
      category: template.category || "classic",
      isActive: template.is_active,
      themeColor: template.theme_color || brandColor,
      isFeatured: template.is_featured,
      sortOrder: template.sort_order,
      coverImagePreview: template.cover_image,
      coverImageFile: null,
      backgroundImagePreview: template.background_image,
      backgroundImageFile: null,
      samplePdfName: fileNameFromUrl(template.sample_pdf) || null,
      samplePdfFile: null,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, field: "cover" | "background") => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToast("Please choose a valid image file.");
      return;
    }
    const url = URL.createObjectURL(file);
    setEditor((prev) =>
      field === "cover"
        ? { ...prev, coverImageFile: file, coverImagePreview: url }
        : { ...prev, backgroundImageFile: file, backgroundImagePreview: url },
    );
  };

  const handleSamplePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.type !== "application/pdf") {
      showErrorToast("Please choose a PDF file.");
      return;
    }
    setEditor((prev) => ({ ...prev, samplePdfFile: file, samplePdfName: file.name }));
  };

  const toggleTemplateActive = async (template: ApiProgramTemplate) => {
    const next = !template.is_active;
    setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, is_active: next } : item)));
    try {
      await apiService.patch(`/api/configurations/program-templates/${template.id}/`, { is_active: next });
      showInfoToast(`${template.name} is now ${next ? "active" : "inactive"}`);
    } catch {
      setTemplates((current) => current.map((item) => (item.id === template.id ? { ...item, is_active: !next } : item)));
      showErrorToast("Could not update the template.");
    }
  };

  const saveTemplate = async () => {
    if (!editor.name.trim()) {
      showErrorToast("Template name is required.");
      return;
    }

    const formData = new FormData();
    formData.append("name", editor.name.trim());
    formData.append("description", editor.description.trim());
    formData.append("category", editor.category);
    formData.append("is_active", String(editor.isActive));
    formData.append("theme_color", editor.themeColor);
    formData.append("is_featured", String(editor.isFeatured));
    formData.append("sort_order", String(editor.sortOrder));
    if (editor.coverImageFile) formData.append("cover_image", editor.coverImageFile);
    if (editor.backgroundImageFile) formData.append("background_image", editor.backgroundImageFile);
    if (editor.samplePdfFile) formData.append("sample_pdf", editor.samplePdfFile);

    setSaving(true);
    try {
      if (editing) {
        await apiService.patch(`/api/configurations/program-templates/${editing.id}/`, formData);
        showSuccessToast("Template updated.");
      } else {
        await apiService.post("/api/configurations/program-templates/", formData);
        showSuccessToast("Template created.");
      }
      setDialogOpen(false);
      await loadAll();
    } catch (error) {
      console.error("Failed to save program template", error);
      showErrorToast("Could not save the template.");
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (template: ApiProgramTemplate) => {
    if (!window.confirm(`Delete "${template.name}"?`)) return;
    try {
      await apiService.delete(`/api/configurations/program-templates/${template.id}/`);
      setTemplates((current) => current.filter((item) => item.id !== template.id));
      showSuccessToast("Template deleted.");
    } catch {
      showErrorToast("Could not delete the template.");
    }
  };

  // These tiers are just which copy counts are offered as quick-pick buttons
  // to customers -- the price itself always comes from the formula, so
  // adding/removing a tier never needs to touch any stored prices.
  const addCopyQuantity = () => {
    const next = (copyQuantities[copyQuantities.length - 1] ?? 0) + 50;
    setCopyQuantities((current) => [...current, next]);
    showInfoToast(`Added ${next} copies tier`);
  };

  const removeCopyQuantity = (copies: number) => {
    if (copyQuantities.length <= 1) {
      showInfoToast("Keep at least one quantity tier.");
      return;
    }
    setCopyQuantities((current) => current.filter((quantity) => quantity !== copies));
    showSuccessToast(`${copies} copies tier removed.`);
  };

  const applyPricing = async () => {
    if (!pricing) return;
    try {
      const response = await apiService.patch<ApiProgramPricingConfig>(
        "/api/configurations/program-pricing-config/mine/",
        {
          base_price: pricing.base_price,
          baseline_pages: pricing.baseline_pages,
          baseline_copies: pricing.baseline_copies,
        },
      );
      setPricing(response.data);
      showSuccessToast("Pricing formula updated.");
    } catch {
      showErrorToast("Could not update the pricing formula.");
    }
  };

  const addAddOn = () => {
    setAddOns((current) => [
      ...current,
      { id: -Date.now(), key: "", label: "New add-on", description: "", price: "0", is_active: true, company: 0 },
    ]);
  };

  const updateAddOn = (id: number, fields: Partial<ApiProgramAddOn>) => {
    setAddOns((current) => current.map((addOn) => (addOn.id === id ? { ...addOn, ...fields } : addOn)));
  };

  const removeAddOn = async (addOn: ApiProgramAddOn) => {
    if (addOn.id < 0) {
      setAddOns((current) => current.filter((item) => item.id !== addOn.id));
      return;
    }
    try {
      await apiService.delete(`/api/configurations/program-add-ons/${addOn.id}/`);
      setAddOns((current) => current.filter((item) => item.id !== addOn.id));
      showSuccessToast("Add-on removed.");
    } catch {
      showErrorToast("Could not remove the add-on.");
    }
  };

  const applyAddOns = async () => {
    try {
      await Promise.all(
        addOns.map((addOn) => {
          const key = addOn.key.trim() || addOn.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 50);
          const payload = { key, label: addOn.label, description: addOn.description, price: addOn.price, is_active: addOn.is_active };
          return addOn.id < 0
            ? apiService.post("/api/configurations/program-add-ons/", payload)
            : apiService.patch(`/api/configurations/program-add-ons/${addOn.id}/`, payload);
        }),
      );
      showSuccessToast("Add-ons updated.");
      await loadAll();
    } catch {
      showErrorToast("Could not save one or more add-ons.");
    }
  };

  const addPaperOption = () => {
    setPaperOptions((current) => [
      ...current,
      { id: -Date.now(), name: "New paper stock", multiplier: "1", weight_label: "", description: "", sort_order: current.length + 1, is_active: true, company: 0 },
    ]);
  };

  const updatePaperOption = (id: number, fields: Partial<ApiProgramPaperOption>) => {
    setPaperOptions((current) => current.map((paper) => (paper.id === id ? { ...paper, ...fields } : paper)));
  };

  const removePaperOption = async (paper: ApiProgramPaperOption) => {
    if (paper.id < 0) {
      setPaperOptions((current) => current.filter((item) => item.id !== paper.id));
      return;
    }
    try {
      await apiService.delete(`/api/configurations/program-paper-options/${paper.id}/`);
      setPaperOptions((current) => current.filter((item) => item.id !== paper.id));
      showSuccessToast("Paper option removed.");
    } catch {
      showErrorToast("Could not remove the paper option.");
    }
  };

  const applyPaperOptions = async () => {
    try {
      await Promise.all(
        paperOptions.map((paper) => {
          const payload = {
            name: paper.name, multiplier: paper.multiplier, weight_label: paper.weight_label,
            description: paper.description, sort_order: paper.sort_order, is_active: paper.is_active,
          };
          return paper.id < 0
            ? apiService.post("/api/configurations/program-paper-options/", payload)
            : apiService.patch(`/api/configurations/program-paper-options/${paper.id}/`, payload);
        }),
      );
      showSuccessToast("Paper options updated.");
      await loadAll();
    } catch {
      showErrorToast("Could not save one or more paper options.");
    }
  };

  if (loading) {
    return <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ minHeight: "100vh", p: { xs: 2, md: 3 } }}>
      {/* Back Button */}
      <Box mb={2}>
        <Button
          onClick={() => navigate("/configurations")}
          startIcon={<BackIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: "999px",
            bgcolor: colors.primary[900],
            color: "text.primary",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.5px",
            px: 2,
            py: 0.6,
            textTransform: "uppercase",
            boxShadow: 0,
            "&:hover": {
              bgcolor: isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.action.hover,
            },
          }}
        >
          Back to configurations
        </Button>
      </Box>

      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color={colors.primary[100]}>
            Program Builder Configuration
          </Typography>
          <Typography color="text.secondary" mt={0.5} variant="body2">
            Configure templates, cover artwork, page background, paper stock, and pricing.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {activeTab === "templates" && hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
              sx={{
                bgcolor: brandColor,
                color: brandTextColor,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: 0,
                "&:hover": { bgcolor: colors.primary[500] },
              }}
            >
              Add template
            </Button>
          )}
        </Stack>
      </Stack>

      {loadError && (
        <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: "14px", bgcolor: alpha(theme.palette.error.main, 0.08) }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography color="error">{loadError}</Typography>
            <Button size="small" onClick={() => void loadAll()}>Retry</Button>
          </Stack>
        </Paper>
      )}

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          boxShadow: 0,
          bgcolor: colors.primary[900],
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value: "templates" | "pricing" | "addons" | "paper") => setActiveTab(value)}
          sx={{
            px: 2,
            "& .MuiTab-root": {
              fontWeight: 700,
              textTransform: "none",
              fontSize: "14px",
              py: 1.75,
            },
            "& .Mui-selected": {
              color: `${brandColor} !important`,
            },
            "& .MuiTabs-indicator": {
              bgcolor: brandColor,
              height: 3,
            },
          }}
        >
          <Tab value="templates" label={`Templates (${templates.length})`} />
          <Tab value="pricing" label="Pricing" />
          <Tab value="addons" label={`Add-ons (${addOns.length})`} />
          <Tab value="paper" label={`Paper options (${paperOptions.length})`} />
        </Tabs>
      </Paper>

      {/* TAB 1: TEMPLATES GRID */}
      {activeTab === "templates" && (
        <Grid container spacing={2.5}>
          {templates.length === 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography color="text.secondary">No templates yet. Click "Add template" to create one.</Typography>
            </Grid>
          )}
          {[...templates]
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((template) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "14px",
                    boxShadow: 0,
                    border: `1px solid ${isDark ? alpha(theme.palette.common.white, 0.08) : theme.palette.divider}`,
                    bgcolor: colors.primary[900],
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      height: 180,
                      position: "relative",
                      bgcolor: isDark ? colors.primary[800] : theme.palette.background.default,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                    }}
                  >
                    {template.cover_image ? (
                      <Box
                        component="img"
                        src={template.cover_image}
                        alt={`${template.name} cover`}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: isDark
                            ? alpha(theme.palette.common.white, 0.03)
                            : theme.palette.background.default,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          No cover image
                        </Typography>
                      </Box>
                    )}

                    {template.is_featured && (
                      <Chip
                        size="small"
                        icon={<StarIcon sx={{ fontSize: 12, color: `${theme.palette.warning.main} !important` }} />}
                        label="Featured"
                        sx={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          bgcolor: alpha(theme.palette.warning.light, 0.9),
                          color: theme.palette.warning.dark,
                          fontWeight: 700,
                          fontSize: 10,
                          zIndex: 2,
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        zIndex: 2,
                        bgcolor: isDark
                          ? alpha(theme.palette.common.black, 0.5)
                          : alpha(theme.palette.common.white, 0.85),
                        borderRadius: "20px",
                        px: 0.8,
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Switch
                        size="small"
                        checked={template.is_active}
                        onChange={() => void toggleTemplateActive(template)}
                        color="success"
                      />
                    </Box>
                  </Box>

                  {/* Card Content */}
                  <Box sx={{ p: 2.5, flexGrow: 1, display: "flex", flexDirection: "column" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="h6" fontWeight={700}>
                        {template.name}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={categoryLabel(template.category)}
                      sx={{
                        alignSelf: "flex-start",
                        mb: 1.5,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: isDark
                          ? alpha(theme.palette.common.white, 0.06)
                          : theme.palette.action.hover,
                      }}
                    />

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, lineHeight: 1.6, flexGrow: 1 }}
                    >
                      {template.description}
                    </Typography>

                    <Stack direction="row" spacing={1} pt={1}>
                      {template.sample_pdf && (
                        <Button
                          size="small"
                          component="a"
                          href={template.sample_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<PdfIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: "none", color: brandColor, fontWeight: 600 }}
                        >
                          Sample
                        </Button>
                      )}
                      {hasPermission(PERMISSIONS.CONFIGURATION_CHANGE) && (
                        <Button
                          size="small"
                          onClick={() => openEdit(template)}
                          startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: "none", color: brandColor, fontWeight: 600 }}
                        >
                          Edit
                        </Button>
                      )}
                      {hasPermission(PERMISSIONS.CONFIGURATION_DELETE) && (
                        <Button
                          size="small"
                          color="error"
                          onClick={() => void deleteTemplate(template)}
                          startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          Delete
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            ))}
        </Grid>
      )}

      {/* TAB 2: PRICING FORMULA */}
      {activeTab === "pricing" && pricing && (
        <Stack spacing={2.5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "14px",
              boxShadow: 0,
              bgcolor: colors.primary[900],
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={0.5}>
              Print Pricing Formula (USD)
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Price scales from one base rate instead of a hand-filled grid: base price x
              (pages / baseline pages) x (copies / baseline copies) x paper multiplier.
              Paper multipliers are set on the Paper options tab.
            </Typography>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Base price"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  value={pricing.base_price}
                  onChange={(e) =>
                    setPricing((current) => (current ? { ...current, base_price: e.target.value } : current))
                  }
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Baseline pages"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  value={pricing.baseline_pages}
                  onChange={(e) =>
                    setPricing((current) => (current ? { ...current, baseline_pages: Math.max(1, Number(e.target.value)) } : current))
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                  label="Baseline copies"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  value={pricing.baseline_copies}
                  onChange={(e) =>
                    setPricing((current) => (current ? { ...current, baseline_copies: Math.max(1, Number(e.target.value)) } : current))
                  }
                />
              </Grid>
            </Grid>

            <Box mt={3}>
              <Button
                variant="contained"
                onClick={() => void applyPricing()}
                sx={{
                  bgcolor: brandColor,
                  color: brandTextColor,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: 0,
                  px: 3,
                  "&:hover": { bgcolor: colors.primary[500] },
                }}
              >
                Apply pricing
              </Button>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: "14px",
              boxShadow: 0,
              bgcolor: colors.primary[900],
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Quantity tiers offered to customers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Computed preview -- these prices aren't stored, they're read live from the formula above.
                </Typography>
              </Box>
              <Button
                startIcon={<AddIcon />}
                onClick={addCopyQuantity}
                sx={{ textTransform: "none", fontWeight: 600, color: brandColor }}
              >
                Add quantity tier
              </Button>
            </Stack>

            <Stack spacing={3}>
              {copyQuantities.map((copies) => (
                <Box key={copies}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography fontWeight={700} variant="subtitle2" color="text.secondary">
                      {copies} COPIES
                    </Typography>
                    <IconButton
                      size="small"
                      title={`Remove ${copies} copies tier`}
                      aria-label={`Remove ${copies} copies tier`}
                      onClick={() => removeCopyQuantity(copies)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Box sx={{ overflowX: "auto" }}>
                    <Box
                      component="table"
                      sx={{
                        width: "100%",
                        borderCollapse: "collapse",
                        "& th, & td": { p: 1.5, textAlign: "center" },
                        "& th": { fontWeight: 700, fontSize: 12, color: "text.secondary" },
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", width: "25%" }}>Stock</th>
                          {PAGE_COUNTS.map((pages) => (
                            <th key={pages}>{pages} Pages</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paperOptions.map((paper) => (
                          <tr key={paper.id}>
                            <td style={{ textAlign: "left" }}>
                              <Typography fontWeight={600} variant="body2">
                                {paper.name}
                              </Typography>
                            </td>
                            {PAGE_COUNTS.map((pages) => (
                              <td key={pages}>
                                <Typography fontWeight={700} variant="body2" sx={{ color: brandColor }}>
                                  ${computePrice(
                                    Number(pricing.base_price),
                                    pricing.baseline_pages,
                                    pricing.baseline_copies,
                                    copies,
                                    pages,
                                    Number(paper.multiplier),
                                  ).toFixed(2)}
                                </Typography>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* TAB 3: ADD-ONS CATALOG */}
      {activeTab === "addons" && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "14px",
            boxShadow: 0,
            bgcolor: colors.primary[900],
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Program Add-ons
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Optional extras customers can attach to a program order. Shown in both the program
                builder and the diaspora pricing calculator.
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={addAddOn}
              sx={{ textTransform: "none", fontWeight: 600, color: brandColor }}
            >
              Add add-on
            </Button>
          </Stack>

          <Stack spacing={2}>
            {addOns.map((addOn) => (
              <Box
                key={addOn.id}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: isDark ? alpha(theme.palette.common.white, 0.03) : theme.palette.background.default,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Label"
                      fullWidth
                      size="small"
                      value={addOn.label}
                      onChange={(e) => updateAddOn(addOn.id, { label: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 5 }}>
                    <TextField
                      label="Description"
                      fullWidth
                      size="small"
                      value={addOn.description}
                      onChange={(e) => updateAddOn(addOn.id, { description: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      label="Price"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{ min: 0 }}
                      value={addOn.price}
                      onChange={(e) => updateAddOn(addOn.id, { price: e.target.value })}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                      <Switch
                        size="small"
                        checked={addOn.is_active}
                        onChange={(e) => updateAddOn(addOn.id, { is_active: e.target.checked })}
                      />
                      <IconButton
                        size="small"
                        title={`Remove ${addOn.label}`}
                        aria-label={`Remove ${addOn.label}`}
                        onClick={() => void removeAddOn(addOn)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>

          <Box mt={3}>
            <Button
              variant="contained"
              onClick={() => void applyAddOns()}
              sx={{
                bgcolor: brandColor,
                color: brandTextColor,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: 0,
                px: 3,
                "&:hover": { bgcolor: colors.primary[500] },
              }}
            >
              Apply add-ons
            </Button>
          </Box>
        </Paper>
      )}

      {/* TAB 4: PAPER OPTIONS */}
      {activeTab === "paper" && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "14px",
            boxShadow: 0,
            bgcolor: colors.primary[900],
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Paper Stock Options
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Price multiplier on the base pricing formula above -- not a separate priced product.
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={addPaperOption}
              sx={{ textTransform: "none", fontWeight: 600, color: brandColor }}
            >
              Add paper option
            </Button>
          </Stack>

          <Stack spacing={2}>
            {paperOptions.map((paper) => (
              <Box
                key={paper.id}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  bgcolor: isDark ? alpha(theme.palette.common.white, 0.03) : theme.palette.background.default,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label="Display name"
                      fullWidth
                      size="small"
                      value={paper.name}
                      onChange={(e) => updatePaperOption(paper.id, { name: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Description"
                      fullWidth
                      size="small"
                      value={paper.description}
                      onChange={(e) => updatePaperOption(paper.id, { description: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      label="Weight / finish"
                      fullWidth
                      size="small"
                      value={paper.weight_label}
                      onChange={(e) => updatePaperOption(paper.id, { weight_label: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <TextField
                      label="Price multiplier"
                      type="number"
                      fullWidth
                      size="small"
                      inputProps={{ min: 0.5, step: 0.1 }}
                      value={paper.multiplier}
                      onChange={(e) => updatePaperOption(paper.id, { multiplier: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                      <Switch
                        size="small"
                        checked={paper.is_active}
                        onChange={(e) => updatePaperOption(paper.id, { is_active: e.target.checked })}
                      />
                      <IconButton
                        size="small"
                        title={`Remove ${paper.name}`}
                        aria-label={`Remove ${paper.name}`}
                        onClick={() => void removePaperOption(paper)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Stack>

          <Box mt={3}>
            <Button
              variant="contained"
              onClick={() => void applyPaperOptions()}
              sx={{
                bgcolor: brandColor,
                color: brandTextColor,
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: 0,
                px: 3,
                "&:hover": { bgcolor: colors.primary[500] },
              }}
            >
              Apply paper options
            </Button>
          </Box>
        </Paper>
      )}

      {/* DIALOG WITH COVER ART & BACKGROUND UPLOAD */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? "Edit Template" : "Add Template"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} pt={1}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 7 }}>
                <TextField
                  label="Template name"
                  fullWidth
                  required
                  size="small"
                  value={editor.name}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Theme category"
                  fullWidth
                  select
                  size="small"
                  value={editor.category}
                  onChange={(e) =>
                    setEditor({ ...editor, category: e.target.value as EditorValue["category"] })
                  }
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              size="small"
              value={editor.description}
              onChange={(e) => setEditor({ ...editor, description: e.target.value })}
            />

            {/* Artwork Upload Row: Cover Image & Page Background */}
            <Grid container spacing={2}>
              {/* Cover Image Upload */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: isDark
                      ? alpha(theme.palette.common.white, 0.03)
                      : theme.palette.background.default,
                  }}
                >
                  <Typography variant="body2" fontWeight={700} mb={1}>
                    Cover / Display Image
                  </Typography>

                  {editor.coverImagePreview ? (
                    <Box sx={{ position: "relative", textAlign: "center", mb: 1 }}>
                      <Box
                        component="img"
                        src={editor.coverImagePreview}
                        alt="Cover preview"
                        sx={{
                          height: 120,
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: "4px",
                          display: "block",
                          mx: "auto",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 90,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 28 }} />
                      <Typography variant="caption">No cover image uploaded</Typography>
                    </Box>
                  )}

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    fullWidth
                    size="small"
                    sx={{ textTransform: "none", color: brandColor }}
                  >
                    {editor.coverImagePreview ? "Change Cover Image" : "Upload Cover Image"}
                    <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "cover")} />
                  </Button>
                </Box>
              </Grid>

              {/* Background Image Upload */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "10px",
                    bgcolor: isDark
                      ? alpha(theme.palette.common.white, 0.03)
                      : theme.palette.background.default,
                  }}
                >
                  <Typography variant="body2" fontWeight={700} mb={1}>
                    Page Background (bg)
                  </Typography>

                  {editor.backgroundImagePreview ? (
                    <Box sx={{ position: "relative", textAlign: "center", mb: 1 }}>
                      <Box
                        component="img"
                        src={editor.backgroundImagePreview}
                        alt="Background preview"
                        sx={{
                          height: 120,
                          width: "100%",
                          objectFit: "cover",
                          borderRadius: "4px",
                          display: "block",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 90,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 0.5,
                        color: "text.secondary",
                        mb: 1,
                      }}
                    >
                      <LayersIcon sx={{ fontSize: 28 }} />
                      <Typography variant="caption">No background texture uploaded</Typography>
                    </Box>
                  )}

                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                    fullWidth
                    size="small"
                    sx={{ textTransform: "none", color: brandColor }}
                  >
                    {editor.backgroundImagePreview ? "Change Background" : "Upload Background"}
                    <input hidden type="file" accept="image/*" onChange={(e) => handleImageUpload(e, "background")} />
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {/* Sample PDF Upload */}
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.03)
                  : theme.palette.background.default,
              }}
            >
              <Typography variant="body2" fontWeight={700} mb={1}>
                Sample PDF (customer preview)
              </Typography>

              {editor.samplePdfName ? (
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
                  <PdfIcon sx={{ fontSize: 28, color: brandColor }} />
                  <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                    {editor.samplePdfName}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  No sample PDF uploaded -- customers won't be able to preview a finished example of this design.
                </Typography>
              )}

              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadIcon />}
                size="small"
                sx={{ textTransform: "none", color: brandColor }}
              >
                {editor.samplePdfName ? "Replace Sample PDF" : "Upload Sample PDF"}
                <input hidden type="file" accept="application/pdf" onChange={handleSamplePdfUpload} />
              </Button>
            </Box>

            {/* Accent Color and Order */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Accent color"
                  type="color"
                  fullWidth
                  size="small"
                  value={editor.themeColor}
                  onChange={(e) => setEditor({ ...editor, themeColor: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Display order"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 1 }}
                  value={editor.sortOrder}
                  onChange={(e) =>
                    setEditor({ ...editor, sortOrder: Math.max(1, Number(e.target.value)) })
                  }
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  size="small"
                  checked={editor.isFeatured}
                  onChange={(e) => setEditor({ ...editor, isFeatured: e.target.checked })}
                />
                <Typography variant="body2">Feature this template</Typography>
              </Stack>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void saveTemplate()}
            disabled={!editor.name.trim() || saving}
            sx={{
              bgcolor: brandColor,
              color: brandTextColor,
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow: 0,
              "&:hover": { bgcolor: colors.primary[500] },
            }}
          >
            {saving ? "Saving..." : editing ? "Save" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgramBuilderTemplates;
